import React, { useState, useEffect } from "react";
import {
    Button,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
} from "@mui/material";
const NewBooking = ({ showNewBooking, setShowNewBooking, donorId }) => {
    const [branches, setBranches] = useState([]);
    const [availableDates, setAvailableDates] = useState([]);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState("");
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default to current month
    const [selectedDate, setSelectedDate] = useState("");
    const [selectedTimeSlot, setSelectedTimeSlot] = useState("");
    const [noDatesAvailable, setNoDatesAvailable] = useState(false); // For showing the error message

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1; // JavaScript months are zero-indexed
    const apiUrl = process.env.REACT_APP_API_BASE_URL;

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const response = await fetch(`${apiUrl}/branches`);
                const data = await response.json();
                if (data.status_code === 0) {
                    setBranches(data.result);
                }
            } catch (error) {
                console.error("Error fetching branches:", error);
            }
        };

        fetchBranches();
    }, []);

    useEffect(() => {
        if (selectedBranch && selectedYear && selectedMonth) {
            const fetchAvailableDates = async () => {
                try {
                    const formattedDate = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
                    const response = await fetch(
                        `${apiUrl}/available-dates?branch_id=${selectedBranch}&date=${formattedDate}`
                    );
                    const data = await response.json();
                    if (data.status_code === 0 && data.result.length > 0) {
                        setAvailableDates(data.result);
                        setNoDatesAvailable(false); // Reset error state if dates are available
                    } else {
                        setAvailableDates([]);
                        setNoDatesAvailable(true); // Show error message when no dates are available
                    }
                } catch (error) {
                    console.error("Error fetching available dates:", error);
                    setNoDatesAvailable(true); // Show error on fetch failure
                }
            };

            fetchAvailableDates();
        }
    }, [selectedBranch, selectedYear, selectedMonth]);

    useEffect(() => {
        if (selectedBranch && selectedDate) {
            const fetchAvailableSlots = async () => {
                try {
                    const response = await fetch(
                        `${apiUrl}/available-slots?branch_id=${selectedBranch}&date=${selectedDate}`
                    );
                    const data = await response.json();
                    if (data.status_code === 0) {
                        setAvailableSlots(data.result.filter(slot => slot.remaining > 0));
                    }
                } catch (error) {
                    console.error("Error fetching available slots:", error);
                }
            };

            fetchAvailableSlots();
        }
    }, [selectedBranch, selectedDate]);

    const handleCreateBooking = async () => {
        try {
            const response = await fetch(`${apiUrl}/book`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    branch_id: selectedBranch,
                    donor_id: donorId,
                    appointment_date: selectedDate,
                    appointment_time: selectedTimeSlot,
                }),
            });

            if (response.ok) {
                const result = await response.json();
                if(result.status_code === 1) {
                    alert(result.message);
                    return;
                }
                console.log("Booking successful!");
                alert("Booking created successfully!");
                setShowNewBooking(false);
            } else {
                console.error("Error booking appointment:", response.statusText);
            }
        } catch (error) {
            console.error("Error booking appointment:", error);
        }
    };

    // Generate month options dynamically based on selected year
    const months = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: new Date(0, i).toLocaleString("default", { month: "long" }),
    })).filter((month) => {
        // Show months based on current or future year
        return selectedYear > currentYear || month.value >= currentMonth;
    });

    // Generate year options (starting from the current year)
    const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

    return (
        <Dialog
            open={showNewBooking}
            onClose={() => setShowNewBooking(false)}
            sx={{
                "& .MuiDialog-paper": {
                    minWidth: "600px", // Standard Bootstrap modal width
                    minHeight: "400px", // Standard Bootstrap modal height
                },
            }}
        >
            <DialogTitle>New Booking</DialogTitle>
            <DialogContent>
                <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel>Branch</InputLabel>
                    <Select
                        value={selectedBranch}
                        onChange={(e) => setSelectedBranch(e.target.value)}
                    >
                        {branches.map((branch) => (
                            <MenuItem key={branch.id} value={branch.id}>
                                {branch.location} - {branch.state}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedBranch && (
                    <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={selectedYear}
                                onChange={(e) => setSelectedYear(e.target.value)}
                            >
                                {years.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl fullWidth>
                            <InputLabel>Month</InputLabel>
                            <Select
                                value={selectedMonth}
                                onChange={(e) => setSelectedMonth(e.target.value)}
                            >
                                {months.map((month) => (
                                    <MenuItem key={month.value} value={month.value}>
                                        {month.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {noDatesAvailable ? (
                    <Typography variant="body1" color="error" sx={{ mt: 2 }}>
                        No dates available for the given selection.
                    </Typography>
                ) : (
                    availableDates.length > 0 && (
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel>Available Dates</InputLabel>
                            <Select
                                value={selectedDate}
                                onChange={(e) => setSelectedDate(e.target.value)}
                            >
                                {availableDates.map((date, index) => (
                                    <MenuItem key={index} value={date}>
                                        {new Date(date).toLocaleDateString()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )
                )}

                {selectedDate && (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <InputLabel>Time</InputLabel>
                        <Select
                            value={selectedTimeSlot}
                            onChange={(e) => setSelectedTimeSlot(e.target.value)}
                        >
                            {availableSlots.map((slot, index) => (
                                <MenuItem key={index} value={slot.slot_time}>
                                    {slot.slot_time}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={() => setShowNewBooking(false)}>Cancel</Button>
                <Button
                    onClick={handleCreateBooking}
                    variant="contained"
                    color="primary"
                    disabled={!selectedBranch || !selectedDate || !selectedTimeSlot}
                >
                    Confirm Booking
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewBooking;
